/**
 * Permet à une page de supprimer l'intro cinématique pour elle seule.
 *
 * Cas d'usage : la 404. L'intro se monte au-dessus du Router, donc la page ne
 * peut pas l'empêcher directement — elle le signale via ce contexte.
 *
 * Le signal passe par useLayoutEffect (et non useEffect) : il est traité avant
 * le premier paint, sinon la vidéo d'intro clignoterait une frame sur la 404.
 */

import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type IntroContextValue = {
  suppressed: boolean;
  setSuppressed: (v: boolean) => void;
};

const IntroContext = createContext<IntroContextValue | null>(null);

export function IntroProvider({ children }: { children: ReactNode }) {
  const [suppressed, setSuppressed] = useState(false);
  // setSuppressed (useState) est stable : la valeur ne change que via suppressed.
  const value = useMemo(() => ({ suppressed, setSuppressed }), [suppressed]);
  return <IntroContext.Provider value={value}>{children}</IntroContext.Provider>;
}

/** Lu par App pour décider d'afficher ou non l'intro. */
export function useIntroSuppressed(): boolean {
  return useContext(IntroContext)?.suppressed ?? false;
}

/** À appeler dans une page qui ne doit pas déclencher l'intro (ex : la 404). */
export function useSuppressIntro(): void {
  const setSuppressed = useContext(IntroContext)?.setSuppressed;
  useLayoutEffect(() => {
    if (!setSuppressed) return;
    setSuppressed(true);
    // Rétabli en quittant la page : les autres pages gardent l'intro.
    return () => setSuppressed(false);
  }, [setSuppressed]);
}
